import axios, { AxiosError, AxiosResponse } from "axios"
import i18next from "i18next"
import { DialogType } from "../models/dialog"
import DialogService from "../services/dialog"
import NavigationService from "../services/navigation"
import StoreService from "../services/store"
import UIService from "../services/UIService"
import UrlUtils from "../utils/urlUtils"
import StringUtils from "../utils/stringUtils"
import { ProviderService } from "../services/provider"
import { NavigationParam } from "../models/navigationParam"

const SUPPORTED_LANGS = ["fr", "en", "it", "es"] as const
const FALLBACK_LANG = "fr"

function normalizeLang(lang: string | undefined): string {
    if (!lang || typeof lang !== "string") return FALLBACK_LANG
    const base = lang.split("-")[0].toLowerCase()
    return SUPPORTED_LANGS.includes(base as (typeof SUPPORTED_LANGS)[number])
        ? base
        : FALLBACK_LANG
}

class Api {
    private static navParam: NavigationParam
    /**
     * Ajoute des intercepteurs Axios pour la gestion des errors globales
     * gestion de toute les erreurs custom
     * (ex. 410, 411, 412 ...)
     */
    public static init(): void {
        const navParamService = ProviderService.getInstance().get(
            "NavigationParamService"
        )
        this.navParam = navParamService ? navParamService.getInstance() : null
        if (!this.navParam) throw Error("No navigationParam found")
        // add request interceptor: append lang from StoreService to every request
        axios.interceptors.request.use(
            async (config) => {
                const lang = await StoreService.getInstance().persistant.get(
                    "lang"
                )
                config.params = {
                    ...(config.params || {}),
                    lang: normalizeLang(lang),
                }
                return config
            },
            (error) => Promise.reject(error)
        )
        // ajout d'un intercepteur de réponses
        axios.interceptors.response.use(
            (
                response: AxiosResponse<any>
            ): AxiosResponse<any> | Promise<AxiosResponse<any>> => {
                // succes
                return Promise.resolve(response)
            },
            async (error: AxiosError) => {
                const axiosError = error
                // error
                try {
                    // on ne prend que les codes erreur 401(session expirée) et 410 (auth_link non valide)
                    if (
                        axiosError.config &&
                        axiosError.response &&
                        (axiosError.response.status === 401 ||
                            axiosError.response.status === 410)
                    ) {
                        UIService.getInstance().showErrorToast(
                            axiosError.response.status === 401
                                ? "Veuillez vous connecter pour accéder à cette page."
                                : "Votre lien n'est plus valide, merci de créer un nouveau lien.",
                            "ApiInterceptor"
                        )
                        const navParam = this.navParam.get()
                        if (
                            navParam?.params?.redir ||
                            (navParam?.params?.link_auth &&
                                axiosError.response.status === 410)
                        )
                            this.navParam.clear()
                        const store: StoreService = StoreService.getInstance()
                        // reset session
                        await store.persistant.delete("token")
                        await store.persistant.delete("refreshToken")
                        store.clearSharedData()
                        NavigationService.getInstance().clearStack()
                        // hide fab button s'il est affiché
                        DialogService.getInstance().send({
                            type: DialogType.SHOW,
                            to: "CwFloatingLocaPanel",
                            params: [{ show: "none" }],
                            sender: "ApiService",
                        })
                        // récuperation de la config de la page à charger
                        const customLandingPage =
                            await StoreService.getInstance().persistant.get(
                                "landingPageConfig"
                            )

                        // la page par defaut est soit une page custom soit dans les var d'env soit login
                        const defaultPage =
                            customLandingPage ||
                            process.env.REACT_APP_DEFAULT_PAGE ||
                            "login"
                        const sluggifyDefaultPage =
                            StringUtils.sluggify(defaultPage)
                        // get redir params
                        const redirString = UrlUtils.getUrlEncode(
                            window.location.hash
                        )
                        // set redir si hash présent ET different de la page default
                        // ET pas code 410 (que des redirections en cas de session expirées)
                        const redirParams =
                            redirString &&
                            redirString.indexOf(sluggifyDefaultPage) === -1 &&
                            axiosError.response.status === 401
                                ? {
                                      redir: redirString,
                                  }
                                : null
                        NavigationService.getInstance().navigateTo(
                            defaultPage,
                            redirParams
                        )
                        // cas code d'erreur custom 411 error verification captcha
                    } else if (axiosError?.response?.status === 411) {
                        UIService.getInstance().showErrorToast(
                            "Vous devez séléctionner la bonne réponse !",
                            "UserService"
                        )
                        // envoie dialog pour refresh le captcha
                        return DialogService.getInstance().send({
                            type: DialogType.REFRESH,
                            to: "CwCaptcha",
                            params: [],
                            sender: "ApiService",
                        })
                        // code d'erreur custom pour le login intelligent
                        // un compte client existant avec la même @ mail
                    } else if (axiosError?.response?.status === 480) {
                        UIService.getInstance().showErrorToast(
                            i18next.t("Account_exists"),
                            "UserService"
                        )
                    }
                    // code d'erreur custom pour le login intelligent
                    // un compte client existant avec la même numéro de teléphone
                    else if (axiosError?.response?.status === 482) {
                        UIService.getInstance().showErrorToast(
                            i18next.t("Account_exists_phone"),
                            "UserService"
                        )
                    }
                    // code d'erreur custom pour la création/ modification entity
                    // une entity existe avec le mm siret
                    else if (axiosError?.response?.status === 481) {
                        UIService.getInstance().showErrorToast(
                            i18next.t("ERR_Entity_SIRET_Failed"),
                            "EntityService"
                        )
                    }
                    // code d'erreur custom pour le login intelligent
                    // plusieurs comptes, il faut se connecter avec le webId
                    else if (axiosError?.response?.status === 405) {
                        UIService.getInstance().showErrorToast(
                            "Votre email est attaché à plusieurs comptes. Veuillez essayer à nouveau avec votre identifiant.",
                            "UserService"
                        )
                    } else if (axiosError?.response?.status === 403) {
                        UIService.getInstance().showErrorToast(
                            i18next.t("Account_permissions"),
                            "UserService"
                        )
                    } else if (axiosError?.response?.status === 412) {
                        // redirection vers la page not found
                        NavigationService.getInstance().navigateTo("notFound")
                    }
                    // code d'erreur de supression de la derniére catégorie (impossible)
                    else if (axiosError?.response?.status === 452) {
                        UIService.getInstance().hideDialog("adminCatList")
                        UIService.getInstance().showErrorToast(
                            i18next.t("Delete_Cat_Error"),
                            "CategoryService"
                        )
                    }
                    // code d'erreur de supression d'un retail point avec TPEs
                    else if (axiosError?.response?.status === 455) {
                        UIService.getInstance().hideDialog(
                            "adminRetailPointList"
                        )
                        UIService.getInstance().showErrorToast(
                            i18next.t("Delete_RetailPoint_TPE_Error"),
                            "RetailPointService"
                        )
                    }
                    // code d'erreur de supression d'un retail point avec profiles
                    else if (axiosError?.response?.status === 456) {
                        UIService.getInstance().hideDialog(
                            "adminRetailPointList"
                        )
                        UIService.getInstance().showErrorToast(
                            i18next.t("Delete_RetailPoint_Profile_Error"),
                            "RetailPointService"
                        )
                    }
                    // code d'erreur de supression d'un groupe pas vide
                    else if (axiosError?.response?.status === 453) {
                        UIService.getInstance().hideDialog("editGroup")
                        UIService.getInstance().showErrorToast(
                            i18next.t("Delete_group_Error"),
                            "GroupService"
                        )
                    }
                    // code d'erreur de supression d'un modéle pas vide'
                    else if (axiosError?.response?.status === 454) {
                        UIService.getInstance().hideDialog("confirmButton")
                        UIService.getInstance().showErrorToast(
                            i18next.t("Delete_Mod_Error"),
                            "ModelService"
                        )
                    }
                    // code d'erreur de supression d'un modéle avec le meme nom
                    else if (axiosError?.response?.status === 406) {
                        UIService.getInstance().hideDialog("confirmButton")
                        UIService.getInstance().showErrorToast(
                            i18next.t("createModelError"),
                            "ModelService"
                        )
                    }
                    // code d'erreur de supression d'un modéle avec le meme nom
                    else if (axiosError?.response?.status === 420) {
                        UIService.getInstance().showErrorToast(
                            i18next.t("signatureLinkAlreadySent"),
                            "ContractService"
                        )
                    } else if (axiosError?.response?.status === 425) {
                        UIService.getInstance().showErrorToast(
                            i18next.t("InvalidDateRange"),
                            "ContractService"
                        )
                    }
                    // code d'erreur de supression d'un modéle avec le meme nom
                    else if (axiosError?.response?.status === 421) {
                        UIService.getInstance().showErrorToast(
                            i18next.t("ERR_No_Credit"),
                            "ContractService"
                        )
                    }
                    // code d'erreur de vérification date d'activation de code promo
                    else if (axiosError?.response?.status === 470) {
                        UIService.getInstance().showErrorToast(
                            i18next.t("ERR_promoCodeNotActive"),
                            "PromoCodeService"
                        )
                    }
                    // code d'erreur de vérification date d'expiration de code promo
                    else if (axiosError?.response?.status === 471) {
                        UIService.getInstance().showErrorToast(
                            i18next.t("ERR_promoCodeExpired"),
                            "PromoCodeService"
                        )
                    }
                    // code d'erreur de vérification utilisation unique de code promo
                    else if (axiosError?.response?.status === 472) {
                        UIService.getInstance().showErrorToast(
                            i18next.t("ERR_promoCodeAlreadyUsed"),
                            "PromoCodeService"
                        )
                    }
                    // code d'erreur de vérification max d'utilisation de code promo
                    else if (axiosError?.response?.status === 473) {
                        UIService.getInstance().showErrorToast(
                            i18next.t("ERR_promoCodeMaxUsage"),
                            "PromoCodeService"
                        )
                    }
                    // code d'erreur de vérification code promo pour pdv
                    else if (axiosError?.response?.status === 474) {
                        UIService.getInstance().showErrorToast(
                            i18next.t("ERR_promoCodeRetailPoint"),
                            "PromoCodeService"
                        )
                    }
                    // code d'erreur de vérification de code promo
                    else if (axiosError?.response?.status === 475) {
                        UIService.getInstance().showErrorToast(
                            i18next.t("ERR_promoCodeNotFound"),
                            "PromoCodeService"
                        )
                    } else if (axiosError?.response?.status === 427) {
                        UIService.getInstance().showErrorToast(
                            i18next.t("InvalidDateRange"),
                            "TransactionService"
                        )
                    }

                    throw Error("Pas de session trouvé")
                } catch (err) {
                    return Promise.reject(error)
                }
            }
        )
    }
}

export default Api
